'use client'

import { type ReactNode, useCallback, useState } from 'react'
import clsx from 'clsx'
import { useCombobox } from 'downshift'
import { useDebouncedCallback } from '../../utils/useDebouncedCallback'
import { TextField, type TextFieldProps } from '../TextField/TextField'
import { inputExpanded, list, listItem } from './AutoComplete.css'

const MIN_CHARACTERS_FOR_SUGGESTIONS = 3
const DEFAULT_DEBOUNCE_DELAY_MS = 300

type Props<Item> = {
  /**
   * Called on idle after input. Must be stable — wrap in useCallback if defined inline.
   */
  getOptions: (input: string) => Promise<Array<Item>>
  onSelectItem?: (item: Item) => void
  /**
   * Converts an item to a plain string. Used to populate the input field after selection
   * and for Downshift's internal state. Must return a string — no markup.
   */
  itemToString?: (item: Item) => string
  /**
   * Renders an item in the dropdown list. Use this when you need rich markup (icons, descriptions,
   * highlighted matches, etc.). Falls back to `itemToString` when omitted.
   */
  itemToOption?: (item: Item) => ReactNode
  minCharactersForSuggestions?: number
  debounceDelayMs?: number
} & Omit<TextFieldProps, 'onValueChange' | 'onClear' | 'onFocus' | 'labelProps'>

/**
 * Relies on Downshift's useCombobox hook for state management, A11y and keyboard navigation.
 * @see https://www.downshift-js.com/use-combobox
 */
export function AutoComplete<Item>({
  getOptions,
  itemToString = (item: Item) => String(item),
  itemToOption,
  onSelectItem,
  minCharactersForSuggestions,
  debounceDelayMs,
  ...textFieldProps
}: Props<Item>) {
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<Array<Item>>([])

  const fetchSuggestions = useCallback(
    async (value: string) => {
      try {
        setIsLoading(true)
        const options = await getOptions(value)
        setOptions(options)
      } catch (error) {
        console.error('AutoSuggest | failed to fetch suggestions', error)
      } finally {
        setIsLoading(false)
      }
    },
    [getOptions],
  )
  const debouncedFetchSuggestions = useDebouncedCallback(fetchSuggestions, debounceDelayMs ?? DEFAULT_DEBOUNCE_DELAY_MS)

  const {
    isOpen,
    highlightedIndex,
    getInputProps,
    getLabelProps,
    getMenuProps,
    getItemProps,
    reset,
    openMenu,
    setInputValue,
  } = useCombobox({
    items: options,
    onInputValueChange(changes) {
      const isSelection =
        changes.type === useCombobox.stateChangeTypes.ItemClick ||
        changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
        changes.type === useCombobox.stateChangeTypes.InputBlur
      // Avoids triggering suggestions retrieval when selecting an item or pressing Enter
      if (isSelection) return

      // Limit min number of characters to trigger suggestions retrieval
      if (changes.inputValue.trim().length >= (minCharactersForSuggestions ?? MIN_CHARACTERS_FOR_SUGGESTIONS)) {
        debouncedFetchSuggestions(changes.inputValue)
        openMenu()
      } else if (options.length > 0) {
        // Input has less than MIN_CHARACTERS_FOR_SUGGESTIONS characters and suggestions are present,
        // therefore we clear the suggestions to avoid showing stale results
        setOptions([])
      }
    },
    onSelectedItemChange(changes) {
      if (changes.selectedItem) {
        onSelectItem?.(changes.selectedItem)
        setOptions([])
      }
    },
    stateReducer(state, action) {
      const { type, changes } = action

      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
          return { ...changes, isOpen: true, highlightedIndex: state.highlightedIndex }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          // Select on [Enter] when only one item is available for selection
          if (options.length === 1) {
            return {
              ...changes,
              inputValue: itemToString(options[0]),
              selectedItem: options[0],
            }
          }
          return changes
        default:
          return changes
      }
    },
    itemToString(item) {
      return item ? itemToString(item) : ''
    },
  })

  const handleClickDelete = () => {
    setOptions([])
    setIsLoading(false)
    reset()
  }

  const isExpanded = isOpen && options.length > 0

  return (
    <div>
      <TextField
        className={clsx(isExpanded && inputExpanded)}
        {...textFieldProps}
        {...getInputProps()}
        loading={textFieldProps.loading || isLoading}
        onValueChange={(value) => setInputValue(value)}
        onClear={handleClickDelete}
        onFocus={() => openMenu()}
        labelProps={getLabelProps()}
      />

      <ul className={list} hidden={!isExpanded} {...getMenuProps()}>
        {options.map((item, index) => (
          <li
            key={`${item}${index}`}
            className={listItem}
            data-highlighted={highlightedIndex === index}
            {...getItemProps({ item, index })}
          >
            {(itemToOption ?? itemToString)(item)}
          </li>
        ))}
      </ul>
    </div>
  )
}
